import { useMemo, forwardRef } from 'react';
import { css } from '@emotion/react';
import dayjs from 'dayjs';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { PopoverMenuItem, styles, Text } from '@frontend/design-system';
import { CallHistoryItem } from '../../providers/softphone-history-provider';
import { useSoftphoneUsers } from '../../providers/softphone-users-provider';
import {
  formatAddress,
  formatPhoneNumber,
  isAnExtensionIntercomCall,
  isExtensionNumber,
  getUsernameFromURI,
} from '../../utils/formatting-utils';

type Props = {
  item: CallHistoryItem;
  onClick: () => void;
  trackingId?: string;
  active?: boolean;
};
export const AutocompleteCallHistory = forwardRef<HTMLLIElement, Props>(
  ({ item, onClick, trackingId, active }, ref) => {
    /* phone */
    const { t } = useTranslation('softphone');
    const display = item.type === 'phone' ? formatPhoneNumber(formatAddress(item.to)) : undefined;

    /* address */
    const getUser = useSoftphoneUsers((ctx) => ctx.getUser);
    const getExtension = useSoftphoneUsers((ctx) => ctx.getExtension);

    const user = useMemo(() => {
      if (!!display) return;
      const username = getUsernameFromURI(item.to);
      const extensionNumber = isExtensionNumber(username)
        ? isAnExtensionIntercomCall(username)
          ? username.slice(1)
          : username
        : undefined;

      return item.type === 'address' && !!extensionNumber
        ? getExtension(extensionNumber)?.name
        : getUser(item.to)?.name;
    }, [item]);

    const displayName = display ?? user ?? item.to;

    return (
      <li
        ref={ref}
        data-trackingid={trackingId}
        className='history'
        key={item.timestamp}
        css={{
          background: active ? theme.colors.neutral70 : 'transparent',
        }}
      >
        <PopoverMenuItem
          css={{
            background: 'transparent',
          }}
          className='list-item'
          onClick={onClick}
          active={active}
        >
          <Text
            color='white'
            css={[
              styles.multilineTruncate(2),
              css`
                max-width: 100%;
              `,
            ]}
          >
            {displayName}
          </Text>
          <Text
            size='small'
            css={css`
              color: ${theme.colors.neutral30};
            `}
          >
            {t('Last Called: {{time}}', { time: dayjs(item.timestamp).format('MMM D, h:mma') })}
          </Text>
        </PopoverMenuItem>
      </li>
    );
  }
);

AutocompleteCallHistory.displayName = 'AutocompleteCallHistory';
